
import React, { useState } from 'react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Search, MapPin, Briefcase, Clock, Upload, Filter, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

// Updated Roles Data - Removed Codex references
const initialRoles = [
    {
        id: 1,
        title: "Founding Agent Product Manager",
        department: "Founding Team",
        type: "Full time",
        location: "San Francisco",
        experience: "3-5 years",
        description: `As a Founding Agent Product Manager, you will own the journey from customer problem to production deployment. You will work directly with enterprise customers and platform partners to identify high-value opportunities, translate complex business workflows into intelligent agent experiences, and lead the design and deployment of Voice Agents.

This is not a traditional product management role. You will be equally comfortable discussing business workflows with a customer executive, designing agent architecture with engineers, configuring agent workflows on platforms, and troubleshooting deployment challenges.

What You Will Do:
• Own the Agent Product Lifecycle - Work with enterprise customers to identify, understand, and prioritize high-value Voice AI opportunities
• Lead Customer-Facing Deployments - Work closely with customer stakeholders, engineering teams, and platform partners
• Build Across the Voice AI Ecosystem - Develop hands-on expertise across multiple Voice AI platforms (Vapi, Retell, LiveKit, Pipecat, ElevenLabs, PolyAI, Parloa)
• Drive Agent Quality and Performance - Establish evaluation frameworks to measure agent accuracy, task completion, latency, and reliability
• Be an Entrepreneur-in-Residence - Represent Vedara at customer offices, partner platform offices, industry events, and ecosystem gatherings

What We're Looking For:
• Experience in product management, technical product management, solutions architecture, or forward-deployed engineering
• Strong technical understanding of software systems, APIs, integrations, and modern AI applications
• Hands-on experience building or deploying AI agents, conversational AI, or Voice AI solutions
• Understanding of LLMs, prompt engineering, tool calling, orchestration, and agent evaluation
• Familiarity with Voice AI platforms such as Vapi, Retell, LiveKit, or similar
• Excellent communication skills with both senior business stakeholders and engineering teams
• Willingness to travel frequently to customer and partner offices

This is an opportunity to help build a category, not just join a company.`
    },
    {
        id: 2,
        title: "Founding Agent Engineer",
        department: "Founding Team",
        type: "Full time",
        location: "San Francisco",
        experience: "3-5 years",
        description: `As a Founding Agent Engineer, you will be responsible for architecting, building, integrating, and deploying Voice Agents into production environments. This is a deeply hands-on engineering role combining strong software engineering fundamentals with practical AI agent and Voice AI experience.

You will work directly with enterprise customers and platform partners, translating complex workflows into production-grade agent systems. You will build across multiple agent platforms and frameworks rather than being tied to a single vendor or technology stack.

What You Will Do:
• Engineer Production-Grade Voice Agents - Design, build, and deploy end-to-end Voice Agents for complex enterprise workflows
• Build Across Multiple Agent Platforms - Work hands-on with Vapi, Retell, LiveKit, Pipecat, ElevenLabs, PolyAI, Parloa, and emerging technologies
• Solve Hard Engineering Problems - Optimize voice latency, response times, reliability, and conversational quality
• Work Directly With Customers - Collaborate with customer engineering teams and travel to customer offices to accelerate implementations
• Be an Entrepreneur-in-Residence - Represent Vedara's engineering capabilities through technical blogs, open-source contributions, and community events

What We're Looking For:
• Strong software engineering background with production-grade software experience
• Proficiency in Python and/or TypeScript/JavaScript
• Hands-on experience building AI agents or agentic workflows using modern LLMs
• Voice AI experience highly valuable - familiarity with platforms like Vapi, Retell, LiveKit, Pipecat, or ElevenLabs
• Understanding of speech-to-text, text-to-speech, real-time audio streaming, WebSockets, SIP, and WebRTC
• Experience with APIs, distributed systems, backend services, and cloud infrastructure
• Entrepreneurial mindset with bias toward shipping and taking ownership
• Willingness to travel to customer and partner offices

If you are excited about building the systems that take Voice AI from impressive demos to enterprise production, we should talk.`
    },
    {
        id: 3,
        title: "Agent Product Manager",
        department: "Product Management",
        type: "Full time",
        location: "United States",
        experience: "4-7 years",
        description: `Drive the vision and execution of Vedara's Voice Agent platform. You will work closely with engineering, customers, and platform partners to define product requirements, roadmap, and go-to-market strategy for production Voice AI systems.

What You'll Do:
• Define product strategy and roadmap for Voice Agent deployment infrastructure
• Gather requirements from enterprise customers and translate them into product features
• Work with engineering teams to prioritize and ship product capabilities
• Analyze agent performance data and identify opportunities for platform improvements
• Build relationships with Voice AI platform partners and influence their roadmaps
• Create product documentation, case studies, and go-to-market materials

What We're Looking For:
• 4-7 years of product management experience, preferably in AI/ML, developer tools, or enterprise software
• Strong technical background with ability to understand agent architectures and APIs
• Experience working with engineering teams in fast-paced environments
• Understanding of conversational AI, LLMs, and agent orchestration
• Excellent communication skills and ability to work with technical and non-technical stakeholders
• Data-driven approach to product decisions`
    },
    {
        id: 4,
        title: "Agent Engineer",
        department: "Engineering",
        type: "Full time",
        location: "United States",
        experience: "3-6 years",
        description: `Build the core infrastructure and deployment tools that power Vedara's Voice Agent implementations. You will work on agent orchestration, evaluation frameworks, observability systems, and reusable components that accelerate enterprise Voice AI deployments.

What You'll Do:
• Build agent orchestration systems and deployment infrastructure
• Develop evaluation frameworks and testing tools for Voice Agents
• Create reusable components and libraries for common agent patterns
• Build observability and monitoring tools for production Voice AI systems
• Work with platform APIs (Vapi, Retell, LiveKit) and abstract common capabilities
• Contribute to open-source tools and frameworks in the Voice AI ecosystem

What We're Looking For:
• 3-6 years of software engineering experience
• Strong proficiency in Python and/or TypeScript
• Experience building production systems with APIs, databases, and cloud infrastructure
• Hands-on experience with LLMs, agent frameworks, and orchestration
• Understanding of Voice AI platforms and real-time communication systems
• Experience with testing, CI/CD, and production observability
• Open-source contributions and technical writing are a plus`
    },
    {
        id: 5,
        title: "Data Scientist",
        department: "Engineering",
        type: "Full time",
        location: "Canada (Toronto)",
        experience: "3-5 years",
        description: `Analyze agent performance data to improve accuracy, reliability, and business outcomes. You will build evaluation frameworks, analyze conversation data, and develop metrics that measure Voice Agent success in production environments.

What You'll Do:
• Analyze conversation data and agent performance metrics
• Build evaluation frameworks to measure task completion, accuracy, and quality
• Develop models to predict agent performance and identify failure modes
• Work with product and engineering to improve agent capabilities based on data insights
• Create dashboards and reports for customers showing agent impact
• Conduct A/B tests and experiments to optimize agent configurations

What We're Looking For:
• 3-5 years of data science experience, preferably in ML/AI products
• Strong proficiency in Python and data analysis tools (pandas, numpy, scikit-learn)
• Experience with LLMs, NLP, and conversational AI metrics
• Ability to translate data insights into product and engineering recommendations
• Experience with SQL, data visualization tools, and statistical analysis
• Strong communication skills to present findings to technical and business stakeholders`
    },
    {
        id: 6,
        title: "Forward Deployed Software Engineer",
        department: "Engineering",
        type: "Full time",
        location: "United States, Canada (Toronto)",
        experience: "3-6 years",
        description: `Work directly with enterprise customers to deploy, customize, and optimize Voice Agent implementations. You will be on the front lines, solving real-world deployment challenges and translating customer needs into technical solutions.

What You'll Do:
• Deploy and configure Voice Agents for enterprise customers
• Build custom integrations with CRMs, contact centers, telephony systems, and APIs
• Troubleshoot production issues and optimize agent performance
• Work alongside customer engineering teams at their offices
• Gather customer requirements and feedback for product improvements
• Build deployment accelerators, templates, and best practices

What We're Looking For:
• 3-6 years of software engineering experience
• Strong proficiency in Python and/or TypeScript/JavaScript
• Experience with APIs, webhooks, enterprise integrations, and cloud platforms
• Hands-on experience with conversational AI or Voice AI implementations
• Excellent debugging and problem-solving skills
• Strong communication skills and comfort working with customers
• Willingness to travel 30-50% to customer sites`
    },
    {
        id: 7,
        title: "AI Research Lead",
        department: "Research and Development",
        type: "Full time",
        location: "United States",
        experience: "5-8 years",
        description: `Lead research initiatives that advance the state of production Voice AI. You will explore new agent architectures, evaluation methods, and deployment techniques that push Voice AI beyond current capabilities.

What You'll Do:
• Lead research projects on agent reasoning, multi-step workflows, and long-horizon tasks
• Develop novel evaluation frameworks for production Voice AI systems
• Experiment with new models, platforms, and techniques for voice agents
• Publish research findings through papers, blog posts, and technical talks
• Collaborate with platform partners on research initiatives
• Translate research breakthroughs into production capabilities

What We're Looking For:
• 5-8 years of AI/ML research or applied research experience
• PhD in Computer Science, AI/ML, or related field (or equivalent experience)
• Strong publication record in relevant conferences (NeurIPS, ICML, ACL, etc.)
• Hands-on experience with LLMs, agent systems, and conversational AI
• Proficiency in Python and ML frameworks (PyTorch, TensorFlow)
• Ability to balance research exploration with practical production requirements
• Strong communication skills and ability to explain complex concepts`
    }
];

const Careers = () => {
  const [resumeFile, setResumeFile] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null); 
  const [expandedRoleId, setExpandedRoleId] = useState(null); 

  // Filter States
  const [departmentFilter, setDepartmentFilter] = useState("All Departments");
  const [locationFilter, setLocationFilter] = useState("All Locations");

  // Filtering Logic
  const filteredRoles = initialRoles.filter(role => {
      const matchDept = departmentFilter === "All Departments" || role.department === departmentFilter;
      const matchLoc = locationFilter === "All Locations" || role.location.includes(locationFilter);
      return matchDept && matchLoc;
  });

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setResumeFile(e.target.files[0]);
    }
  };

  const openApplyDialog = (role) => {
      setSelectedRole(role);
      setIsDialogOpen(true);
  };

  const handleEmailResume = async () => {
    if (!resumeFile) {
      toast.error("Please attach a resume first.");
      return;
    }
    
    // Get form values
    const nameInput = document.querySelector("input[name='applicant-name']");
    const emailInput = document.querySelector("input[name='applicant-email']");
    
    if (!nameInput?.value || !emailInput?.value) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setLoading(true);
    
    try {
        // Send application to backend API
        const appliedFor = selectedRole ? selectedRole.title : "General Application";
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/careers/apply`, { 
            method: "POST", 
            headers: { "Content-Type": "application/json" }, 
            body: JSON.stringify({ 
                name: nameInput.value, 
                email: emailInput.value, 
                role: appliedFor, 
                resume_filename: resumeFile.name 
            }) 
        });
        
        if (!response.ok) { 
            const errorData = await response.json(); 
            throw new Error(errorData.detail || "Failed to submit application"); 
        }
        
        const result = await response.json();
        toast.success(`Application for ${appliedFor} sent to hello@vedara.ai!`);
        setIsDialogOpen(false);
        setResumeFile(null);
        setSelectedRole(null);
        
        // Reset form fields
        if (nameInput) nameInput.value = "";
        if (emailInput) emailInput.value = "";
    } catch (e) {
        toast.error("Failed to send resume. Please try again.");
    } finally {
        setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col font-sans">
      <Navbar />
      
      {/* Main Content Area */}
      <main className="flex-grow pt-32 pb-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto w-full">
        
        {/* Header Section */}
        <div className="mb-16">
            <section className="mb-16">
                <h1 className="text-3xl md:text-4xl font-bold text-white mb-6">About Vedara</h1>
                <div className="prose prose-lg text-gray-400 space-y-4">
                    <p>
                        Vedara builds production-grade Voice Agents for the world's most ambitious organizations. We forward-deploy elite Voice AI teams to design, build, and scale conversational systems that handle real customer interactions from support and sales to healthcare and insurance workflows. Our focus is simple: turn advances in voice AI into reliable, low-latency production systems that deliver measurable business outcomes.
                    </p>
                </div>
            </section>
        </div>

        {/* Job Listings */}
        <section id="open-roles">
            <h2 className="text-2xl font-bold text-white mb-8 border-b border-gray-800 pb-4">Open Positions</h2>
            
            {/* Filters Section */}
            <div className="flex flex-col md:flex-row gap-4 mb-8">
                <div className="w-full md:w-1/3">
                    <Label className="text-gray-400 mb-2 block">Department</Label>
                    <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                        <SelectTrigger className="bg-[#0f172a] border-gray-700 text-white">
                            <SelectValue placeholder="All Departments" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0f172a] border-gray-800 text-white">
                            <SelectItem value="All Departments">All Departments</SelectItem>
                            <SelectItem value="Founding Team">Founding Team</SelectItem>
                            <SelectItem value="Product Management">Product Management</SelectItem>
                            <SelectItem value="Engineering">Engineering</SelectItem>
                            <SelectItem value="Research and Development">Research & Development</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div className="w-full md:w-1/3">
                    <Label className="text-gray-400 mb-2 block">Location</Label>
                    <Select value={locationFilter} onValueChange={setLocationFilter}>
                        <SelectTrigger className="bg-[#0f172a] border-gray-700 text-white">
                            <SelectValue placeholder="All Locations" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0f172a] border-gray-800 text-white">
                            <SelectItem value="All Locations">All Locations</SelectItem>
                            <SelectItem value="San Francisco">San Francisco</SelectItem>
                            <SelectItem value="United States">United States</SelectItem>
                            <SelectItem value="Canada">Canada</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Roles List */}
            <div className="space-y-4">
                {filteredRoles.length > 0 ? (
                    filteredRoles.map((role) => (
                        <div key={role.id} className="group bg-[#0f172a] hover:bg-[#1e293b] border border-gray-800 transition-all rounded-lg overflow-hidden">
                            {/* Role Header - Always Visible */}
                            <div className="p-6">
                                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                                    <div className="flex-1">
                                        <h4 className="text-xl font-bold text-white group-hover:text-blue-400 transition-colors">
                                            {role.title}
                                        </h4>
                                        <div className="flex items-center gap-4 text-sm text-gray-500 mt-2 flex-wrap">
                                            <span className="flex items-center gap-1"><Briefcase size={14}/> {role.department}</span>
                                            <span className="flex items-center gap-1"><MapPin size={14}/> {role.location}</span>
                                            <span className="flex items-center gap-1"><Clock size={14}/> {role.type}</span>
                                            {role.experience && <span className="flex items-center gap-1 text-blue-400 font-semibold">Experience: {role.experience}</span>}
                                        </div>
                                        
                                        {/* Show first 2 lines of description as preview */}
                                        <div className="text-gray-400 mt-3 text-sm leading-relaxed">
                                            {role.description.split('\n').slice(0, 2).join(' ').substring(0, 200)}...
                                        </div>
                                    </div>
                                    <div className="flex gap-2 flex-shrink-0 mt-2 md:mt-0">
                                        <Button 
                                            onClick={() => setExpandedRoleId(expandedRoleId === role.id ? null : role.id)}
                                            variant="outline" 
                                            className="rounded-full border-gray-700 text-gray-400 hover:text-white hover:bg-gray-800"
                                        >
                                            {expandedRoleId === role.id ? (
                                                <>Hide Details <ChevronUp size={16} className="ml-1" /></>
                                            ) : (
                                                <>Learn More <ChevronDown size={16} className="ml-1" /></>
                                            )}
                                        </Button>
                                        <Button 
                                            onClick={() => openApplyDialog(role)}
                                            variant="outline" 
                                            className="rounded-full border-blue-600 text-blue-400 hover:text-white hover:bg-blue-600"
                                        >
                                            Apply Now
                                        </Button>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Expandable Full Description */}
                            {expandedRoleId === role.id && (
                                <div className="px-6 pb-6 border-t border-gray-800 pt-6 animate-in slide-in-from-top">
                                    <div className="text-gray-300 text-sm leading-relaxed whitespace-pre-line max-w-4xl">
                                        {role.description}
                                    </div>
                                    <div className="mt-6 pt-6 border-t border-gray-800">
                                        <Button 
                                            onClick={() => openApplyDialog(role)}
                                            className="bg-blue-600 hover:bg-blue-700 text-white px-8"
                                        >
                                            Apply for this role
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))
                ) : (
                    <div className="text-center py-10 text-gray-500 bg-[#0f172a] rounded-lg border border-gray-800">
                        <Filter className="mx-auto h-10 w-10 mb-3 opacity-50" />
                        <p>No open positions found matching your filters.</p>
                        <Button 
                            variant="link" 
                            className="text-vedara-blue-400" 
                            onClick={() => {setDepartmentFilter("All Departments"); setLocationFilter("All Locations");}}
                        >
                            Clear Filters
                        </Button>
                    </div>
                )}
            </div>

             {/* General Application Area */}
             <div className="mt-12 p-8 bg-[#0f172a] border border-gray-800 rounded-xl text-center">
                <p className="text-gray-400 mb-6">Don't see a role that fits? We are always looking for exceptional talent.</p>
                <Button 
                    onClick={() => openApplyDialog(null)}
                    className="bg-vedara-blue-600 hover:bg-vedara-blue-500 text-white px-8 py-2 rounded-full flex items-center gap-2 mx-auto"
                >
                    <Upload size={16} /> Email Resume
                </Button>
            </div>

            {/* Application Dialog */}
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent className="bg-[#0f172a] border-gray-800 text-white sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>
                            {selectedRole ? `Apply for ${selectedRole.title}` : "Send us your Resume"}
                        </DialogTitle>
                        <DialogDescription className="text-gray-400">
                            Attach your resume below. We will email it directly to hello@vedara.ai.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                        <div className="grid w-full items-center gap-1.5">
                            <Label htmlFor="applicant-name">Full Name *</Label>
                            <Input 
                                id="applicant-name" 
                                name="applicant-name"
                                type="text" 
                                placeholder="John Doe"
                                required
                                className="bg-background border-gray-700 text-white"
                            />
                        </div>
                        <div className="grid w-full items-center gap-1.5">
                            <Label htmlFor="applicant-email">Email Address *</Label>
                            <Input 
                                id="applicant-email" 
                                name="applicant-email"
                                type="email" 
                                placeholder="john@example.com"
                                required
                                className="bg-background border-gray-700 text-white"
                            />
                        </div>
                        <div className="grid w-full items-center gap-1.5">
                            <Label htmlFor="resume">Resume (PDF/DOCX) *</Label>
                            <Input id="resume" type="file" onChange={handleFileChange} className="bg-background border-gray-700 text-white file:text-white" />
                        </div>
                    </div>
                    <div className="flex justify-end">
                        <Button 
                            onClick={handleEmailResume} 
                            disabled={loading}
                            className="bg-vedara-blue-600 hover:bg-vedara-blue-500 text-white"
                        >
                            {loading ? "Sending..." : "Send Application"}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

        </section>

      </main>
      <Footer />
    </div>
  );
};

export default Careers;
